import { docMeta, expiryText, fmtDate } from '@components/Agreements/agreement';
import ActionIconButton from '@components/Common/ActionIconButton';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import { uploadToS3Direct } from '@components/Common/directUpload';
import EmptyState from '@components/Common/EmptyState';
import { SectionCard } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import FileUpload from '@components/Common/FileUpload/FileUpload';
import FormField from '@components/Common/FormField';
import ToggleField from '@components/Common/ToggleField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import {
  agreementsByEmployer,
  deleteAgreementMutation,
  recordAgreementMutation,
  updateAgreementMutation,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';

import './AgreementsPanel.scss';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const AGREEMENT_COLUMNS = ['title', 'signed', 'status', 'actions'];

const AGREEMENT_BUCKET = 'trymintly-agreement';

interface Agreement {
  _id: string;
  title?: string;
  fileName?: string;
  fileSizeBytes?: number;
  s3Key: string;
  signedAt?: string;
  expiresAt?: string;
  status?: string;
  visibleToEmployer?: boolean;
  uploadedAt?: string;
  uploadedBy?: string;
}

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUPERSEDED', label: 'Superseded' },
  { value: 'TERMINATED', label: 'Terminated' },
];

/** 25MB. Scanned contracts are an image per page and much larger than a born-digital one. */
const MAX_FILE_BYTES = 25 * 1024 * 1024;

/**
 * The signed agreements on file for one client.
 *
 * Its own component rather than another branch in EmployerWorkspace's tab map, which is already
 * a thousand lines: this one owns an upload, a form and a list, and inlining it would bury all
 * three.
 *
 * The upload is two steps on purpose. The file goes to the private agreements bucket first, and
 * only a successful upload produces the object key that the record is filed against. Doing it the
 * other way round leaves a row pointing at an object that does not exist, which reads to everyone
 * afterwards as a corrupt record rather than a failed upload.
 *
 * <p>The list is `MintlyTable`, the same component `/agreements` uses. It was 80 lines of
 * hand-built `Box` cards with their own border, radius and chip colours, showing the same object
 * the other page shows, and the two agreed on nothing but the date format. The rebuild also gets
 * the phone card-collapse this panel never had, and puts the destructive action behind a
 * confirmation, which it also never had.
 */
const AgreementsPanel: React.FC<{ employerId: string }> = ({ employerId }) => {
  const [rows, setRows] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [signedAt, setSignedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [shareNow, setShareNow] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Agreement | null>(null);
  // The row whose status menu is open, and the button it hangs from.
  const [statusMenu, setStatusMenu] = useState<{ row: Agreement; anchor: HTMLElement } | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(agreementsByEmployer(employerId));
      setRows(data?.getAgreementsByEmployer ?? []);
    } catch {
      setError('Could not load agreements for this employer.');
    } finally {
      setLoading(false);
    }
  }, [employerId]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    if (!file) return;
    /*
     * Title and signed-on carry an asterisk, so something has to enforce them. Before this the
     * only guard was `disabled={!file}`: an agreement could be filed with no description and no
     * signature date, which is exactly the metadata the list is read by.
     */
    const missing: { [key: string]: string } = {};
    if (!title.trim()) missing.title = 'Say what this document is';
    if (!signedAt) missing.signedAt = 'A signature date is required';
    setFieldErrors(missing);
    if (Object.keys(missing).length > 0) {
      document.getElementById(missing.title ? 'title' : 'signedAt')?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      /*
       * The server decides the key, not this.
       *
       * It sanitises the name and composes the per-employer prefix itself, so a key guessed
       * here can differ from where the object actually lands. It did: the upload route reduces
       * every key to its basename, so an `agreements/<employerId>/...` path was flattened to
       * the bucket root while this recorded the path it had asked for, and the record pointed
       * at an object that was not there.
       *
       * The timestamp is still ours so a re-signed contract sits beside its predecessor rather
       * than overwriting the record of what was agreed before.
       */
      // Direct-to-S3 presigned POST. The server composes the object under this employer's prefix
      // (agreements/<employerId>/...) from a validated id and signs a form only for that exact key;
      // the bucket is private, so a countersigned contract is never world-readable. The returned
      // key is what the record stores.
      const key = await uploadToS3Direct(AGREEMENT_BUCKET, file, {
        fileName: `${Date.now()}-${file.name}`,
        employerId,
      });

      await fetchGraphQLApiData(
        recordAgreementMutation({
          employerId,
          s3Key: key,
          fileName: file.name,
          fileSizeBytes: file.size,
          title: title.trim(),
          signedAt,
          expiresAt,
          visibleToEmployer: shareNow,
        }),
      );

      setFile(null);
      setTitle('');
      setSignedAt('');
      setExpiresAt('');
      setShareNow(false);
      await load();
    } catch {
      setError('Could not upload that agreement. The file was not saved.');
    } finally {
      setBusy(false);
    }
  };

  const setVisibility = async (id: string, visible: boolean) => {
    setBusy(true);
    try {
      await fetchGraphQLApiData(updateAgreementMutation({ _id: id, visibleToEmployer: visible }));
      await load();
    } catch {
      setError('Could not change who can see that agreement.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id: string, status: string) => {
    setBusy(true);
    try {
      await fetchGraphQLApiData(updateAgreementMutation({ _id: id, status }));
      await load();
    } catch {
      setError('Could not change that agreement.');
    } finally {
      setBusy(false);
    }
  };

  /*
   * Deleting a signed agreement used to be one click with no confirmation of any kind. It is a
   * countersigned commercial document, the row is the only record the console has of it, and the
   * button sat next to a status dropdown people use routinely.
   */
  const remove = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await fetchGraphQLApiData(deleteAgreementMutation(pendingDelete._id));
      setPendingDelete(null);
      await load();
    } catch {
      setError('Could not remove that agreement.');
    } finally {
      setBusy(false);
    }
  };

  const download = async (row: Agreement) => {
    try {
      // Presigned: the bucket is private and has no public-read exception, so there is no URL
      // that works without one.
      const { data } = await axios.get(
        `/api/s3/buckets/${AGREEMENT_BUCKET}/download/${encodeURIComponent(row.s3Key)}`,
      );
      const url = data?.url ?? data;
      if (typeof url === 'string') window.open(url, '_blank', 'noopener');
    } catch {
      setError('Could not open that document.');
    }
  };

  if (loading) return <SkeletonLoader columns={AGREEMENT_COLUMNS.length} />;

  return (
    <>
      {error && (
        <Box sx={{ mb: 'var(--spacing-md)' }}>
          <ErrorMessage message={error} />
        </Box>
      )}

      <SectionCard title="Upload a signed agreement" icon={<DescriptionOutlinedIcon />}>
        <Typography
          sx={{ fontSize: 'var(--font-size-sm)', color: 'var(--tx-mid)', mb: 'var(--spacing-sm)' }}
        >
          PDF or Word, up to 25MB. Scanned contracts are usually an image per page and much
          larger than a born-digital one, which is why the ceiling here is higher than elsewhere.
          Nothing is shared with the employer unless you say so.
        </Typography>

        {/* The shared drop zone, which also enforces the type and size limits it is given. This
            was a `Button component="label"` wrapping a hidden `<input type="file">`, which is
            the thing FileUpload exists to stop each page rebuilding. */}
        <FileUpload
          disabled={busy}
          file={file ?? ''}
          maxFileSize={MAX_FILE_BYTES}
          supportedFileTypes=".pdf,.doc,.docx"
          title="Drop the signed document, or choose a file"
          onFileDrop={(f) => setFile(f)}
          onDelete={() => setFile(null)}
        />

        <Box sx={{ display: 'grid', gap: 'var(--spacing-sm)', mt: 'var(--spacing-sm)' }}>
          <FormField
            required
            name="title"
            label="What this is"
            placeholder="Premium Hiring MSA, 2026 renewal"
            value={title}
            error={!!fieldErrors.title}
            helperText={fieldErrors.title}
            onChange={(_n, v) => setTitle(String(v))}
          />
          <FormField
            required
            name="signedAt"
            label="Signed on"
            type="date"
            value={signedAt}
            error={!!fieldErrors.signedAt}
            helperText={fieldErrors.signedAt}
            onChange={(_n, v) => setSignedAt(String(v))}
          />
          <FormField
            required={false}
            name="expiresAt"
            label="Expires (optional)"
            type="date"
            value={expiresAt}
            onChange={(_n, v) => setExpiresAt(String(v))}
          />
          <ToggleField
            label="Show this to the employer straight away"
            checked={shareNow}
            onChange={setShareNow}
          />
        </Box>

        <Box sx={{ mt: 'var(--spacing-sm)' }}>
          <Button variant="contained" disabled={!file || busy} onClick={upload}>
            {busy ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </SectionCard>

      <SectionCard
        title="On file"
        icon={<DescriptionOutlinedIcon />}
        meta={rows.length === 1 ? '1 agreement' : `${rows.length} agreements`}
      >
        {rows.length === 0 ? (
          <EmptyState
            compact
            icon={<DescriptionOutlinedIcon />}
            title="Nothing signed on file yet"
            body="Upload the countersigned document above. Nothing is shared with the employer until you say so."
          />
        ) : (
          <MintlyTable
            /* Unpaginated: this is one client's paperwork, which is a handful of rows and has a
               natural ceiling. The cross-client list at /agreements pages, because that one does
               not. */
            pagination={{
              enabled: false,
              page: 0,
              rowsPerPage: rows.length || 1,
              totalNumberOfRows: rows.length,
              handleChangePage: () => {},
            }}
            rows={rows.map((r) => ({ ...r, id: r._id }))}
            /* This table lives in the workspace's narrow centre column. On the 768-1023px
               tablet band four columns still cramp, so render stacked cards there instead. */
            cardBelow="tablet"
            /*
             * Four columns, not six. This sits in the workspace's centre column, which is narrow,
             * and six columns meant every one of them truncated: the file column showed a clipped
             * object key, "Employer can see" clipped to "Employer ...", and its toggle label to
             * "Sham". Two of the six were labelled form fields, so the header said STATUS and the
             * cell said "Status" directly underneath it.
             *
             * The file name and size now ride under the title, where there is room for them, and
             * the expiry rides under the signed date. Same information, half the columns.
             */
            selectors={AGREEMENT_COLUMNS}
            tableHeadRow={{
              title: <>Agreement</>,
              signed: <>Signed</>,
              status: <>Status</>,
              actions: <>&nbsp;</>,
            }}
            /*
             * Every cell is one line that ellipsises (the table is fixed-layout), so nothing here
             * may need more width than its column. The row used to carry a status dropdown and a
             * labelled switch: the switch was squeezed to a sliver of its track beside the word
             * "Shared", and the line under the title was the record's raw file name, a hex id cut
             * off mid-string. Now each cell reads as text and every change is an icon action.
             */
            tableBodyRow={{
              title: (row: Agreement) => (
                <Tooltip title={row.fileName || ''} placement="top-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Box className="agreement-title">
                      {row.title || 'Untitled agreement'}
                    </Box>
                    <Box className="agreement-meta">{docMeta(row)}</Box>
                  </Box>
                </Tooltip>
              ),
              signed: (row: Agreement) => (
                <Box>
                  {row.signedAt ? (
                    <Box>{fmtDate(row.signedAt)}</Box>
                  ) : (
                    <Box className="agreement-meta">Date not recorded</Box>
                  )}
                  {row.expiresAt && (
                    <Box className="agreement-meta">{expiryText(row.expiresAt)}</Box>
                  )}
                </Box>
              ),
              /* What it is and who can see it, as words. The two settings that change them are
                 actions on the right, not form controls inside a cell. */
              status: (row: Agreement) => (
                <Box className="agreement-status">
                  <CRMStatusChip status={row.status || 'ACTIVE'} />
                  <Box className="agreement-meta agreement-visibility">
                    {row.visibleToEmployer ? (
                      <VisibilityOutlinedIcon fontSize="inherit" />
                    ) : (
                      <VisibilityOffOutlinedIcon fontSize="inherit" />
                    )}
                    {row.visibleToEmployer ? 'Employer can see' : 'Internal only'}
                  </Box>
                </Box>
              ),
              actions: (row: Agreement) => (
                <Box className="agreement-actions">
                  <ActionIconButton
                    size="small"
                    label={
                      row.visibleToEmployer
                        ? 'Stop sharing with the employer'
                        : 'Share with the employer'
                    }
                    icon={
                      row.visibleToEmployer ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )
                    }
                    disabled={busy}
                    onClick={() => setVisibility(row._id, !row.visibleToEmployer)}
                  />
                  <ActionIconButton
                    size="small"
                    label="Change status"
                    icon={<EditNoteOutlinedIcon fontSize="small" />}
                    disabled={busy}
                    onClick={(e) => setStatusMenu({ row, anchor: e.currentTarget })}
                  />
                  <ActionIconButton
                    size="small"
                    label="Open the document"
                    icon={<DownloadIcon fontSize="small" />}
                    onClick={() => download(row)}
                  />
                  <ActionIconButton
                    size="small"
                    color="error"
                    label="Remove this agreement"
                    icon={<DeleteOutlineIcon fontSize="small" />}
                    disabled={busy}
                    onClick={() => setPendingDelete(row)}
                  />
                </Box>
              ),
            }}
          />
        )}
      </SectionCard>

      <Menu
        anchorEl={statusMenu?.anchor ?? null}
        open={!!statusMenu}
        onClose={() => setStatusMenu(null)}
      >
        {STATUS_OPTIONS.map((o) => (
          <MenuItem
            key={o.value}
            selected={(statusMenu?.row.status || 'ACTIVE') === o.value}
            onClick={() => {
              const row = statusMenu?.row;
              setStatusMenu(null);
              if (row && (row.status || 'ACTIVE') !== o.value) setStatus(row._id, o.value);
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove this agreement?"
        body={
          <>
            <strong>{pendingDelete?.title || pendingDelete?.fileName}</strong> is removed from this
            employer&apos;s record and from the Agreements list. The document itself stays in the
            bucket, but nothing in the console points at it afterwards.
          </>
        }
        actionLabel="Remove"
        busy={busy}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
};

export default AgreementsPanel;
